// /var/www/translyx-cat/src/lib/cat/translationMemory.ts
import { supabase } from '../supabase';

export interface TMEntry {
  id: string;
  source_text: string;
  target_text: string;
  source_language: string;
  target_language: string;
  owner_id: string;
  project_id: string | null;
  usage_count: number;
  created_at: string;
}

export interface TMMatch {
  entry: TMEntry;
  score: number; // 0–100
  matchType: 'exact' | 'fuzzy';
}

// ─── Similarity Algorithm ────────────────────────────────────────────────────

export function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
}

function wordOverlapScore(a: string, b: string): number {
  const setA = new Set(a.toLowerCase().split(/\s+/).filter(Boolean));
  const setB = new Set(b.toLowerCase().split(/\s+/).filter(Boolean));
  const intersection = [...setA].filter(w => setB.has(w)).length;
  const union = new Set([...setA, ...setB]).size;
  return union === 0 ? 0 : intersection / union;
}

export function calculateSimilarity(a: string, b: string): number {
  const aNorm = a.toLowerCase().trim();
  const bNorm = b.toLowerCase().trim();
  if (aNorm === bNorm) return 100;
  const maxLen = Math.max(aNorm.length, bNorm.length);
  if (maxLen === 0) return 100;

  // Levenshtein-based similarity (0–1)
  const editSim = 1 - levenshteinDistance(aNorm, bNorm) / maxLen;
  // Word-overlap similarity (0–1)
  const wordSim = wordOverlapScore(aNorm, bNorm);
  // Weighted blend: 60% edit, 40% word overlap
  const blended = editSim * 0.6 + wordSim * 0.4;
  return Math.round(blended * 100);
}

// ─── Core TM Service ─────────────────────────────────────────────────────────

export const translationMemory = {
  /**
   * Add or update a TM entry.
   * Uses SELECT then INSERT/UPDATE to respect RLS — never upsert.
   */
  normalizeLang(code: string): string {
    return code.toLowerCase().trim()
  },

  async addEntry(
    sourceText: string,
    targetText: string,
    sourceLang: string,
    targetLang: string,
    projectId?: string
  ): Promise<boolean> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const src = sourceText.trim();
      const tgt = targetText.trim();
      if (!src || !tgt) return false;

      // Check if an entry already exists for this source text + language pair
      const { data: existing, error: fetchErr } = await supabase
        .from('cat_translation_memory')
        .select('id, usage_count')
        .eq('source_text', src)
        .eq('source_language', this.normalizeLang(sourceLang))
        .eq('target_language', this.normalizeLang(targetLang))
        .eq('owner_id', user.id)
        .maybeSingle();

      if (fetchErr) {
        console.error('TM fetch error:', fetchErr.message);
      }

      if (existing) {
        // UPDATE — safe with RLS
        const { error: updateErr } = await supabase
          .from('cat_translation_memory')
          .update({
            target_text: tgt,
            usage_count: (existing.usage_count ?? 0) + 1,
          })
          .eq('id', existing.id);
        if (updateErr) {
          console.error('TM update error:', updateErr.message);
          return false;
        }
      } else {
        // INSERT new entry
        const { error: insertErr } = await supabase
          .from('cat_translation_memory')
          .insert({
            source_text: src,
            target_text: tgt,
            source_language: this.normalizeLang(sourceLang),
            target_language: this.normalizeLang(targetLang),
            owner_id: user.id,
            project_id: projectId ?? null,
            usage_count: 1,
          });
        if (insertErr) {
          console.error('TM insert error:', insertErr.message);
          return false;
        }
      }

      return true;
    } catch (err) {
      console.error('TM addEntry exception:', err);
      return false;
    }
  },

  /**
   * Search TM for matches above minScore threshold.
   * Fetches up to 500 entries for the language pair, scores locally.
   */
  async search(
    sourceText: string,
    sourceLang: string,
    targetLang: string,
    minScore = 70
  ): Promise<TMMatch[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      const src = sourceText.trim();
      if (!src) return [];

      const normSrc = this.normalizeLang(sourceLang)
      const normTgt = this.normalizeLang(targetLang)
      const baseSrc = normSrc.split('-')[0]
      const baseTgt = normTgt.split('-')[0]

      const fetchEntries = async (sl: string, tl: string) => {
        const { data, error } = await supabase
          .from('cat_translation_memory')
          .select('*')
          .eq('source_language', sl)
          .eq('target_language', tl)
          .eq('owner_id', user.id)
          .order('usage_count', { ascending: false })
          .limit(500)
        if (error) { console.error('TM search error:', error.message); return [] }
        return data ?? []
      }

      let entries = await fetchEntries(normSrc, normTgt)
      // Fallback 1: strip region subtag (pt-pt → pt)
      if (!entries.length && (normSrc !== baseSrc || normTgt !== baseTgt)) {
        entries = await fetchEntries(baseSrc, baseTgt)
      }
      // Fallback 2: add pt-pt variant when bare 'pt' finds nothing
      if (!entries.length && normTgt === 'pt') {
        entries = await fetchEntries(normSrc, 'pt-pt')
      }
      if (!entries.length && normTgt === 'fr') {
        entries = await fetchEntries(normSrc, 'fr-fr')
      }
      if (!entries.length && normSrc === 'pt') {
        entries = await fetchEntries('pt-pt', normTgt)
      }
      if (!entries?.length) return [];

      const matches: TMMatch[] = [];
      for (const entry of entries) {
        const score = calculateSimilarity(src, entry.source_text);
        if (score >= minScore) {
          matches.push({
            entry: entry as TMEntry,
            score,
            matchType: score === 100 ? 'exact' : 'fuzzy',
          });
        }
      }

      return matches.sort((a, b) => b.score - a.score).slice(0, 6);
    } catch (err) {
      console.error('TM search exception:', err);
      return [];
    }
  },

  /** Paginated list of all entries for the current user */
  async getAllEntries(
    page = 0,
    pageSize = 50,
    searchQuery = ''
  ): Promise<{ entries: TMEntry[]; count: number }> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return { entries: [], count: 0 };

      let query = supabase
        .from('cat_translation_memory')
        .select('*', { count: 'exact' })
        .eq('owner_id', user.id);

      if (searchQuery.trim()) {
        query = query.or(
          `source_text.ilike.%${searchQuery}%,target_text.ilike.%${searchQuery}%`
        );
      }

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (error) {
        console.error('TM getAllEntries error:', error.message);
        return { entries: [], count: 0 };
      }
      return { entries: (data as TMEntry[]) ?? [], count: count ?? 0 };
    } catch (err) {
      console.error('TM getAllEntries exception:', err);
      return { entries: [], count: 0 };
    }
  },

  /** Get all entries (no pagination) — for export */
  async getAllEntriesForExport(): Promise<TMEntry[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];
      const { data } = await supabase
        .from('cat_translation_memory')
        .select('*')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });
      return (data as TMEntry[]) ?? [];
    } catch {
      return [];
    }
  },

  async deleteEntry(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('cat_translation_memory')
        .delete()
        .eq('id', id);
      return !error;
    } catch {
      return false;
    }
  },

  async deleteAllEntries(): Promise<boolean> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;
      const { error } = await supabase
        .from('cat_translation_memory')
        .delete()
        .eq('owner_id', user.id);
      return !error;
    } catch {
      return false;
    }
  },

  /** Import from TMX 1.4 XML string. Batch inserts for performance. */
  async importTMX(
    tmxContent: string,
    sourceLang: string,
    targetLang: string,
    onProgress?: (imported: number, total: number) => void
  ): Promise<number> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const parser = new DOMParser();
      const doc = parser.parseFromString(tmxContent, 'text/xml');
      const tus = Array.from(doc.querySelectorAll('tu'));

      // Parse all translation units first
      const pairs: { source: string; target: string }[] = [];
      for (const tu of tus) {
        let source = '';
        let target = '';
        tu.querySelectorAll('tuv').forEach(tuv => {
          const lang = tuv.getAttribute('xml:lang') || tuv.getAttribute('lang') || '';
          const seg = tuv.querySelector('seg')?.textContent ?? '';
          if (lang.toLowerCase().startsWith(sourceLang.toLowerCase())) source = seg.trim();
          if (lang.toLowerCase().startsWith(targetLang.toLowerCase())) target = seg.trim();
        });
        if (source && target) pairs.push({ source, target });
      }

      if (!pairs.length) return 0;

      const normSrc = this.normalizeLang(sourceLang);
      const normTgt = this.normalizeLang(targetLang);
      const BATCH_SIZE = 50;
      let totalImported = 0;

      for (let i = 0; i < pairs.length; i += BATCH_SIZE) {
        const batch = pairs.slice(i, i + BATCH_SIZE).map(p => ({
          source_text: p.source,
          target_text: p.target,
          source_language: normSrc,
          target_language: normTgt,
          owner_id: user.id,
          project_id: null,
          usage_count: 1,
        }));

        // Use upsert with ignoreDuplicates to skip existing entries cleanly
        const { error } = await supabase
          .from('cat_translation_memory')
          .upsert(batch, {
            onConflict: 'owner_id,source_text,source_language,target_language',
            ignoreDuplicates: true,
          });

        if (!error) {
          totalImported += batch.length;
        } else {
          // Fallback: insert individually for this batch if upsert fails
          for (const entry of batch) {
            const { error: insertErr } = await supabase
              .from('cat_translation_memory')
              .insert(entry);
            if (!insertErr) totalImported++;
          }
        }

        if (onProgress) onProgress(Math.min(i + BATCH_SIZE, pairs.length), pairs.length);
      }

      return totalImported;
    } catch (err) {
      console.error('TMX import error:', err);
      return 0;
    }
  },

  /** Export to TMX 1.4 XML string */
  exportTMX(entries: TMEntry[], sourceLang: string, targetLang: string): string {
    const date = new Date().toISOString();
    const tus = entries
      .map(
        e => `  <tu>
    <tuv xml:lang="${sourceLang}"><seg>${escXml(e.source_text)}</seg></tuv>
    <tuv xml:lang="${targetLang}"><seg>${escXml(e.target_text)}</seg></tuv>
  </tu>`
      )
      .join('\n');

    return `<?xml version="1.0" encoding="UTF-8"?>
<tmx version="1.4">
  <header creationtool="TranslyxCAT" creationtoolversion="1.0" datatype="PlainText" segtype="sentence" adminlang="en-US" srclang="${sourceLang}" creationdate="${date}"/>
  <body>
${tus}
  </body>
</tmx>`;
  },
};

function escXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}